import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { useAppSelector } from "integrations/redux/hooks"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import {
    AggregateField,
    InferMultiResult,
    InferQueryResult,
    OrgScopedResource,
    ResourceName,
    TimeBucket,
    TypedQueryPayload,
    ValidPath,
    castQueryResults,
} from "@nova/queryable"
import useSWR, { mutate } from "swr"

export const invalidatePilotTrackingQuery = (resource: ResourceName) =>
    mutate((key) => {
        if (!Array.isArray(key)) return false
        const first = key[0]
        if (
            typeof first === "object" &&
            first !== null &&
            (first as { resource?: string }).resource === resource
        )
            return true
        if (typeof first === "string") {
            try {
                const parsed = JSON.parse(first) as Record<string, unknown>
                return Object.values(parsed).some(
                    (q) =>
                        typeof q === "object" &&
                        q !== null &&
                        (q as { resource?: string }).resource === resource
                )
            } catch {
                return false
            }
        }
        return false
    })

const fetchQuery = <
    R extends OrgScopedResource,
    const S extends ReadonlyArray<ValidPath<R>> | undefined = undefined,
    const A extends ReadonlyArray<AggregateField<R>> | undefined = undefined,
    const T extends ReadonlyArray<TimeBucket<R>> | undefined = undefined,
>(
    orgId: string,
    query: TypedQueryPayload<R, S, A, T>
): Promise<InferQueryResult<R, S, A, T>[]> => {
    return (
        fetchApiGateway("post /org/:orgId/fleet", {
            params: { orgId },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            body: query as any,
        }) as unknown as Promise<Record<string, unknown>[]>
    ).then((rows) => castQueryResults(rows, query) as InferQueryResult<R, S, A, T>[])
}

export function usePilotTrackingQuery<
    R extends OrgScopedResource,
    const S extends ReadonlyArray<ValidPath<R>> | undefined = undefined,
    const A extends ReadonlyArray<AggregateField<R>> | undefined = undefined,
    const T extends ReadonlyArray<TimeBucket<R>> | undefined = undefined,
>(query: TypedQueryPayload<R, S, A, T>) {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const { data, isLoading, error } = useSWR(orgId ? [query, orgId] : null, ([q, oid]) =>
        fetchQuery(oid, q)
    )

    return {
        data,
        isLoading,
        error,
    }
}

export const fetchMultiQuery = <
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const T extends Record<string, TypedQueryPayload<any, any, any, any>>,
>(
    orgId: string,
    queries: T
): Promise<InferMultiResult<T>> => {
    return (
        fetchApiGateway("post /org/:orgId/batchFleet", {
            params: { orgId },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            body: queries as any,
        }) as unknown as Promise<Record<string, Record<string, unknown>[]>>
    ).then((response) => {
        const result: Record<string, unknown[]> = {}
        for (const key of Object.keys(queries)) {
            result[key] = castQueryResults(response[key] ?? [], queries[key])
        }
        return result as InferMultiResult<T>
    })
}

export function useBatchPilotTrackingQuery<
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const T extends Record<string, TypedQueryPayload<any, any, any, any>>,
>(queries: T) {
    const orgId = useAppSelector(selectActiveWorkspaceId())

    const cacheKey = orgId ? [JSON.stringify(queries), orgId] : null

    const { data, isLoading, isValidating, error } = useSWR(
        cacheKey,
        ([, oid]) => fetchMultiQuery(oid, queries),
        { keepPreviousData: true }
    )

    return {
        data,
        isLoading,
        isValidating,
        error,
    }
}
